import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Message, WorkoutLog, Program } from '../types';
import { useDietStatus } from '../hooks/useDietStatus';
import { NATIVE_PROGRAMS } from '../data/programs';
import { PendingPhotosModal } from './PendingPhotosModal';
import { useChatSync } from '../hooks/useChatSync';
import { useTranslation } from 'react-i18next';

// Helper: UUID v4 Fallback for insecure contexts (HTTP mobile testing)
function generateUUIDv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

type MarcStatus = 'offline' | 'online' | 'ausente' | 'visto' | 'typing';

const AIAssistant: React.FC = () => {
  const { t } = useTranslation();
  const { user, subscriptionStatus, loading, onboardingCompleted, marcAvatarUrl, userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // V500: CLOUD CONTEXT FOR AI
  const { status: hookDietStatus, targetCompletionTime, isGenerating, plan, generationType, needsDietReveal } = useDietStatus();
  
  const dietStatusRef = useRef(hookDietStatus);
  const targetTimeRef = useRef(targetCompletionTime);

  useEffect(() => { dietStatusRef.current = hookDietStatus; }, [hookDietStatus]);
  useEffect(() => { targetTimeRef.current = targetCompletionTime; }, [targetCompletionTime]);

  // DASHBOARD TRIGGER RE-SYNC (V210): Ensure triggers fire on navigation
  useEffect(() => {
    if (isHistoryLoadedRef.current) {
      console.log("[AIAssistant] Navigation detected, re-checking triggers...");
      checkPremiumTriggers();
    }
  }, [location.pathname]);

  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [viewport, setViewport] = useState<{ height: number; offsetTop: number } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    
    const handleResize = () => {
      if (window.visualViewport) {
        setViewport({
          height: window.visualViewport.height,
          offsetTop: window.visualViewport.offsetTop
        });
      }
    };
    
    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);
    handleResize();
    
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('chat-open');
    } else {
      document.body.classList.remove('chat-open');
    }
    return () => document.body.classList.remove('chat-open');
  }, [isOpen]);

  const [input, setInput] = useState('');
  // V1000: INTEGRATED CHAT SYNC HOOK
  const { messages, setMessages, isHistoryLoaded, loadHistory, addLocalSeenIds } = useChatSync(user?.id);
  const messagesRef = useRef<Message[]>([]);
  messagesRef.current = messages;

  // Precision Tick for re-evaluating time-based message reveals (Optimized from 1s interval)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const now = Date.now();
    let nextMilestone = Infinity;

    // Find the next closest milestone (seenStart, typingStart, or revealAt)
    messages.forEach(m => {
      let revealAt = m.revealTime || 0;
      // Only schedule milestones for future unread messages
      if (revealAt > 0 && !m.seen && m.content !== '[TYPING_INDICATOR]' && !(m.content || '').includes('[TYPING_INDICATOR]')) {
        // Match the +3000 padding from visibleMessages for diet macros
        const isDietMsg = m.content && (m.content.includes('plan nutricional') || m.content.includes('ajustes en tu dieta') || m.content.includes('¡Hecho'));
        if (isDietMsg) revealAt += 3000;

        const estimatedTypingMs = Math.max(3000, Math.min(20000, (m.content || '').length * 50));
        const typingStart = revealAt - estimatedTypingMs;
        const seenStart = typingStart - 5000;

        // Wake up at whatever the NEXT upcoming event is
        if (seenStart > now && seenStart < nextMilestone) nextMilestone = seenStart;
        if (typingStart > now && typingStart < nextMilestone) nextMilestone = typingStart;
        if (revealAt > now && revealAt < nextMilestone) nextMilestone = revealAt;
      }
    });

    if (nextMilestone !== Infinity) {
      const delay = Math.max(10, nextMilestone - now);
      const timeoutId = setTimeout(() => {
        setTick(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, tick]);

  const isHistoryLoadedRef = useRef(false);
  useEffect(() => { isHistoryLoadedRef.current = isHistoryLoaded; }, [isHistoryLoaded]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<MarcStatus>('ausente');



  // VISIBILITY MEMO: Filter out technical tags and ensure PERFECT CHRONOLOGY (V95)
  const isDietForceRevealed = (() => {
    const isComplete = hookDietStatus === 'completed';
    const isTimerGone = !targetCompletionTime;
    const isPendingReveal = needsDietReveal;
    const forceRevealed = sessionStorage.getItem('aura_diet_chat_revealed') === 'true';
    
    // V335 FIX: Prevent the final message from jumping early during the backend sync.
    // If the success screen hasn't been shown yet (isPendingReveal === true) OR the loading screen is still active,
    // NEVER force-reveal the message, even if the timer momentarily drops.
    return isComplete && ((isTimerGone && !isPendingReveal && !isGenerating) || forceRevealed);
  })();

  const visibleMessages = React.useMemo(() => {
    const getEffectiveTime = (m: Message) => {
      if (m.role === 'model' && m.revealTime) return m.revealTime;
      return m.createdAt ? new Date(m.createdAt).getTime() : 0;
    };

    const now = Date.now();

    const result = [...messages]
      .filter(m => {
        const upper = (m.content || '').toUpperCase();
        if (!upper) return false;
        // Strict Filter: Hide system tags but ALLOW System Errors for debugging
        if (upper.includes('[PROCESSING') || upper.includes('[TYPING]') || upper.includes('[TYPING_INDICATOR]') || upper.includes('[MARK_SEEN]')) return false;
        if (upper.includes('[SISTEMA') && !upper.includes('[SISTEMA ERR]')) return false; // Hide system tags unless it's an error

        let revealAt = m.revealTime || 0;

        // V335: UI PRIORITY PADDING
        // If it's a Diet message, we add a 3s padding to its natural reveal time.
        // This ensures Dashboard's strict 1s-ticker hits `Date.now() >= target` first, 
        // plays its 0.8s fade-in overlay, and dispatches `aura_diet_reveal_show`.
        // The event then triggers the bypass block below to instantly pop the message in perfect sync.
        const isDietMsg = m.content.includes('plan nutricional') || m.content.includes('ajustes en tu dieta') || m.content.includes('¡Hecho');
        if ((isDietMsg || m.isDietReadyMsg) && revealAt > 0) {
          revealAt += 3000;
        }

        if (revealAt > 0 && revealAt > now) {
          // V500: Always show if the user has already seen it, so it doesn't disappear on app restart
          if (m.seen) return true;

          // V334: EARLY REVEAL FOR SKIPPED/SYNCED TIMERS
          // If it's a diet completion message, but the user skipped the timer or it reached naturally,
          // aura_diet_status will be 'completed' and aura_target_completion_time will be cleared.
          if (m.isDietReadyMsg && isDietForceRevealed) {
            return true; // Force reveal early exactly in sync with Dashboard Overlay
          }
          return false;
        }
        return true;
      })
      .sort((a, b) => getEffectiveTime(a) - getEffectiveTime(b));

    // V999: EXTREMELY AGGRESSIVE VERBOSE LOGGING FOR DEBUGGING
    if (messages.length > 0) {
      console.log(`[AIAssistant:visibleMessages] Total Messages in State: ${messages.length}. Visible: ${result.length}. Now: ${now}`);
      messages.forEach((m, i) => {
        const revealAt = m.revealTime || 0;
        console.log(`  -> Msg ${i}: ID=${m.id}, Role=${m.role}, isFuture=${m.isFuture}, revealAt=${revealAt}, DiffNow=${now - revealAt}ms`);
        console.log(`  -> Content snippet: ${m.content.slice(0, 30)}...`);
      });
    }

    return result;
  }, [messages, tick, isDietForceRevealed]);

  // V1301: ACTION TRIGGER WATCHER
  // Trigger diet updates EXACTLY when the [ACTION:UPDATE_DIET] message is visually revealed to the user,
  // respecting the realistic "Slow Chat" wait times, as requested by UX requirements.
  useEffect(() => {
    if (visibleMessages.length === 0 || !user?.id) return;
    
    // Scan recent visible messages in case they revealed simultaneously
    const recentRevealed = visibleMessages.slice(-5);
    
    recentRevealed.forEach(msg => {
      // FIX V2028: Check safe actions array instead of raw content because chatParser strips the text
      if (msg.role === 'model' && (msg.actions?.includes('UPDATE_DIET') || (msg.content && /\[ACTION:\s*UPDATE[-_\s]+DIET\s*\]/i.test(msg.content)))) {
        const triggerKey = `aura_action_triggered_${msg.id}`;
        const hasTriggeredLocal = localStorage.getItem(triggerKey);
        
        // V2032: PREVENT GHOST TRIGGERS ON APP RESTART
        // Ensure this specific message ID hasn't triggered an update globally for this device.
        const triggeredList = JSON.parse(localStorage.getItem('aura_triggered_diet_msgs') || '[]');
        const isAlreadyTriggeredGlobal = triggeredList.includes(msg.id);
        
        // V2106: THE "1-SECOND BUG" FIX
        // We completely removed the `isRecent` mathematical logic (which was causing bugs if the user reloaded within 5 minutes).
        // Instead, we strictly rely on the `isRealtime` flag set by `useChatSync`.
        // If a message comes from history, it will NEVER trigger the UI, period.
        
        if (!hasTriggeredLocal && !isAlreadyTriggeredGlobal && msg.isRealtime) {
             console.log(`[AIAssistant] Visual Reveal of UPDATE_DIET for LIVE msg ${msg.id}. Emitting event.`);
             localStorage.setItem(triggerKey, 'true');
             const newList = [...triggeredList, msg.id].slice(-20); // Keep last 20
             localStorage.setItem('aura_triggered_diet_msgs', JSON.stringify(newList));
             localStorage.setItem('aura_last_diet_trigger_msg_id', msg.id);
             
             // 1. Instantly Block UI Locally (to respect the narrative delay)
             sessionStorage.removeItem('aura_diet_chat_revealed');
             
             // V6035: Synchronously inject the optimistic state into the new aura_user_data cache
             // so useDietStatus sees it immediately and mounts the loading screen, blocking the UI.
             const visualNow = Date.now();
             
             try {
                 const cachedStr = localStorage.getItem('aura_user_data');
                 const cachedObj = cachedStr ? JSON.parse(cachedStr) : {};
                 
                 // V6039 FIX: Robustly detect if it's an update by checking the cache directly,
                 // because 'plan' might be null during the first render if the fast cache was cleared.
                 const isUpdateVisual = (!!plan && Object.keys(plan).length > 0) || !!cachedObj.nutritionPlan || !!cachedObj.diet;
                 const genType = isUpdateVisual ? 'update' : 'new';
                 
                 // V6050: Use the message creation timestamp (which represents the exact revealTime)
                 // to synchronize the frontend timer precisely with the cloud trigger start.
                 const rawCreated = msg.createdAt || (msg as any).created_at;
                 const processStart = rawCreated ? new Date(rawCreated).getTime() : visualNow;
                 const targetTime = processStart + (isUpdateVisual ? 1800000 : 5400000);

                 cachedObj.aura_diet_status = 'trigger_update';
                 cachedObj.aura_diet_process_start = processStart;
                 cachedObj.aura_target_completion_time = targetTime;
                 cachedObj.aura_generation_type = genType;
                 localStorage.setItem('aura_user_data', JSON.stringify(cachedObj));
             } catch (e) {
                 console.error("Failed to inject optimistic cache:", e);
             }
             
             window.dispatchEvent(new Event('aura_diet_updated'));

             // V2086: Removed cinematic redirect per user request.

             // 2. V3005: FRONTEND DB TRIGGER REMOVED
             // The frontend no longer makes DB updates. Trigger is handled server-side on process-chat.
             console.log(`[AIAssistant] Visual Reveal handled. Diet trigger initiated in cloud during process-chat.`);
        }
      }
    });
  }, [visibleMessages, user?.id]);

  // UNREAD COUNT: V117: Only count VISIBLE unread messages to avoid ghost notifications
  // Using both visibleMessages AND messages for robust dependency tracking
  const unreadCount = React.useMemo(() => {
    return visibleMessages.filter(m => m.role === 'model' && !m.seen).length;
  }, [visibleMessages, messages]);

  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
  const [newMsgMarkerId, setNewMsgMarkerId] = useState<string | null>(null);
  const [isCheckinReviewActive, setIsCheckinReviewActive] = useState(false);
  // (Status management consolidated in V97: BIDIRECTIONAL STATUS CONTROLLER)
  const isPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing' || subscriptionStatus === 'pro';
  
  // V3000: FIX CHAT LOCK LEAK
  // Block chat if backend is generating, if there's no plan yet, OR if the fake visual timer is active
  const isTimerRunning = (() => {
    try {
      const cachedStr = localStorage.getItem('aura_user_data');
      if (cachedStr) {
        const parsed = JSON.parse(cachedStr);
        const target = parsed.aura_target_completion_time;
        const start = parsed.aura_diet_process_start;
        const isProcessStarted = !start || Date.now() >= parseInt(start);
        return isProcessStarted && parsed.aura_diet_status === 'completed' && target && parseInt(target) > Date.now();
      }
    } catch(e){}
    return false;
  })();
  const isEffectivelyGenerating = isGenerating || isTimerRunning || (isPremium && !plan);

  // V1400: CHECK-IN REVIEW TRIGGER WATCHER
  // Automatically unlock the chat when Marc's check-in response arrives.
  // We use robust ID anchoring to completely eliminate client-server clock drift issues.
  useEffect(() => {
    if (!isCheckinReviewActive || visibleMessages.length === 0 || !user?.id) return;
    
    // ... rest of the checkin logic remains the same
    const anchorId = localStorage.getItem('aura_checkin_anchor_id');
    if (!anchorId) return; // Wait for the anchor to be seeded by the save function
    
    // Check if the very last visible message from Marc is DIFFERENT from our anchor.
    // Since user is blocked from texting, the absolute next model message must be the resolution!
    const recentModelMsgs = visibleMessages.filter(m => m.role === 'model');
    if (recentModelMsgs.length === 0) return;
    
    const latestModelMsg = recentModelMsgs[recentModelMsgs.length - 1];
    
    // Fallback: If anchor was created via clock fallback, just unlock (better to unlock early than permanently freeze)
    if (anchorId.startsWith('fallback_time_')) {
      const fallbackTime = parseInt(anchorId.replace('fallback_time_', ''), 10);
      const msgTime = latestModelMsg.createdAt ? new Date(latestModelMsg.createdAt).getTime() : 0;
      if (msgTime > fallbackTime) {
         localStorage.removeItem('aura_checkin_review_active');
         localStorage.removeItem('aura_checkin_anchor_id');
         setIsCheckinReviewActive(false);
      }
      return;
    }

    // Primary ID logic: if Marc sent anything *other* than the old anchor, he resolved it!
    if (latestModelMsg.id && latestModelMsg.id !== anchorId && anchorId !== 'none') {
      console.log(`[AIAssistant] Marc responded after check-in. Unlocking chat.`);
      localStorage.removeItem('aura_checkin_review_active');
      localStorage.removeItem('aura_checkin_anchor_id');
      setIsCheckinReviewActive(false);
    } else if (anchorId === 'none' && latestModelMsg.id) {
      // Edge case: first message ever
      console.log(`[AIAssistant] Marc's first message arrived. Unlocking chat.`);
      localStorage.removeItem('aura_checkin_review_active');
      localStorage.removeItem('aura_checkin_anchor_id');
      setIsCheckinReviewActive(false);
    }
  }, [visibleMessages, isCheckinReviewActive, user?.id]);

  // V1500: FORCED RE-RENDER ON DIET REVEAL
  useEffect(() => {
    const handleDietReveal = () => {
      console.log("[AIAssistant] Dashboard dispatched aura_diet_reveal_show. Forcing tick to sync pending messages.");
      setTick(prev => prev + 1);
    };
    window.addEventListener('aura_diet_reveal_show', handleDietReveal);
    return () => window.removeEventListener('aura_diet_reveal_show', handleDietReveal);
  }, []);

  // V2100: PRECISION TIME-BASED REVEAL ENGINE
  // This logic was removed because it is redundant and caused memory leaks.
  // The precision timeout engine is already handled natively at the top of the file (lines 68-100).

  // Sync status with offline time on mount/interval
  useEffect(() => {
    const checkTime = () => {
      const h = new Date().getHours();
      // Marc is offline between 23:00 and 07:00
      if (h >= 23 || h < 7) setStatus('offline');
      else setStatus(prev => prev === 'offline' ? 'ausente' : prev);
    };
    checkTime();
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Check visibility requirements
  const userDataRaw = localStorage.getItem('aura_user_data');
  const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
  const hasCompletedQuestionnaire = onboardingCompleted || (!!userData && userData.onboardingCompleted !== false);
  // RELAXED VISIBILITY: Show chat on Welcome and Questionnaire too, only hide on Auth pages
  const isPublicPage = ['/login', '/register'].includes(location.pathname);

  /* REFS */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const pendingResponseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingTimeoutResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setOnlineTemporarily = () => {
    const h = new Date().getHours();
    if (h >= 23 || h < 7) {
      setStatus('offline');
      return;
    }
    setStatus('online');
    if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current);
    onlineTimerRef.current = setTimeout(() => {
      setStatus(prev => prev === 'online' ? 'ausente' : prev);
    }, 60000);
  };

  // Redundant proactively firing shouldMarkSeen effect removed.
  // Seen statuses are now strictly deferred to the `[MARK_SEEN]` sentinel or realtime DB updates.
  const isComponentMountedRef = useRef(true);
  useEffect(() => {
    isComponentMountedRef.current = true;
    return () => { isComponentMountedRef.current = false; };
  }, []);

  // STABLE REF PATTERN: Ensure Interval/Listeners always call the LATEST version
  const checkPremiumTriggersRef = useRef<() => void>(() => { });
  const isProcessingTriggerRef = useRef(false);
  // (Removed duplicate messagesRef)

  const sendBufferRef = useRef<string[]>([]);
  const sendTimerRef = useRef<any>(null);
  const thinkingTimerRef = useRef<any>(null);
  const isRequestingRef = useRef<boolean>(false);
  const lastFillerRef = useRef<string | null>(null);
  const isGeneratingResponseRef = useRef<boolean>(false);

  // V1002: FALLBACK LOCAL CACHE FOR READ MESSAGES
  // Fixes the issue where the `mark-seen` Edge Function fails to deploy or execute in cloud,
  // causing messages to revert to `seen: false` on page refresh.
  // Local cache and history logic handled by useChatSync hook

  // Welcomes are now handled server-side via Edge Functions and DB Triggers.
  // This ensures they complete even if the user logs out.

  // loadHistory is now destructured from useChatSync hook

  const [delayedSeen, setDelayedSeen] = useState<Record<string, boolean>>({});

  const checkPremiumTriggers = async (historyOverride?: Message[]) => {
    // 1. SLEEP GUARD: Marc is completely silent between 23:00 and 06:00
    // EXCEPTION: Welcome sequences must always trigger regardless of time.
    const hour = new Date().getHours();
    const isSleepTime = hour >= 23 || hour < 6;

    // We need to resolve these here to know if we should bypass sleep
    const currentHistory = (historyOverride || messagesRef.current) || [];
    const pendingWelcome = localStorage.getItem('aura_pending_welcome_sequence');
    const isFirstRun = currentHistory.length === 0;

    if (isSleepTime && !pendingWelcome && !isFirstRun) {
      // Only block if it's NOT a welcome scenario
      return;
    }

    // 2. CONCURRENCY & READINESS GUARD
    const ready = isHistoryLoadedRef.current || !!historyOverride;
    if (isProcessingTriggerRef.current || !ready) {
      console.log(`[AIAssistant] Guard blocked: processing=${isProcessingTriggerRef.current}, ready=${ready}`);
      return;
    }

    // V1000: EVENT GAP RECOVERY (HTTP vs WEBSOCKET RACE CONDITION Patcher)
    // If the Edge Function takes 2s to insert, but loadHistory fires at 1.5s (missing the new messages),
    // AND Realtime WebSocket takes until 2.5s to connect... the initial messages are SILENTLY LOST.
    // This safety net catches the flag and fiercely polls until the DB catches up.
    if (pendingWelcome) {
      // Check if we already received the welcome messages by looking for recent AI messages
      // containing "Bienvenido al equipo Aura Pro" or "Tardaré entre 6 y 12 horas".
      const hasWelcomeArrived = currentHistory.some(m => 
        m.role === 'model' && 
        (m.content.includes('equipo Aura Pro') || m.content.includes('un rato en tenerlo todo ajustado') || m.content.includes('Dame un rato') || m.content.includes('Puedes pegarte las palizas que quieras') || m.content.includes('Aura Pro team') || m.content.includes('train as hard as you want') || m.content.includes('I just reviewed your file'))
      );

      if (!hasWelcomeArrived) {
        console.warn("[AIAssistant] Event Gap Detected: Pending welcome but new Pro messages not found in state. Engaging fallback poll...");
        isProcessingTriggerRef.current = true;
        let attempts = 0;

        const pollTimer = setInterval(async () => {
          attempts++;
          if (attempts > 12) { // Give up after 12s
            clearInterval(pollTimer);
            isProcessingTriggerRef.current = false;
            localStorage.removeItem('aura_pending_welcome_sequence');
            console.warn("[AIAssistant] Event Gap Recovery gave up after 12 seconds.");
            return;
          }

          console.log(`[AIAssistant] Polling DB for missing welcome messages... (Attempt ${attempts}/12)`);
          const fallbackHistory = await loadHistory();

          // Check if poll succeeded
          if (fallbackHistory && fallbackHistory.some(m => m.role === 'model' && (m.content.includes('equipo Aura Pro') || m.content.includes('un rato') || m.content.includes('Puedes pegarte las palizas que quieras') || m.content.includes('Aura Pro team') || m.content.includes('train as hard as you want') || m.content.includes('I just reviewed your file') || m.content.includes('Acabo de ver tu ficha')))) {
            console.log(`[AIAssistant] SUCCESS: Missing welcome messages recovered via polling!`);
            clearInterval(pollTimer);
            localStorage.removeItem('aura_pending_welcome_sequence');
            isProcessingTriggerRef.current = false;
            // Welcome recovered, user will see the notification indicator natively
          }
        }, 1200);

        return; // Stop evaluating legacy triggers until polled
      } else {
        // Welcome arrived already (via normal realtime or fast loadHistory)
        localStorage.removeItem('aura_pending_welcome_sequence');
        // Welcome arrived, user will see the notification indicator natively
      }
    }

    const isDashboard = location.pathname === '/dashboard';

    console.log(`[AIAssistant] Checking triggers... History: ${currentHistory.length}, Premium: ${isPremium}, PendingWelcome: ${pendingWelcome}`);

    // V630: We ONLY trigger if there is a pending diet or legacy cleanup.
    // BasicWelcomeScreen handles the welcome sequence trigger natively with `force: true`.
    // The previous overlapping blocks here caused catastrophic race conditions resulting in 
    // duplicated and out-of-order welcome messages.

    const activeWelcome = localStorage.getItem('aura_premium_welcome_trigger');

    // 6. LEGACY WELCOME CLEANUP (Should not block)
    if (activeWelcome) {
      localStorage.removeItem('aura_premium_welcome_trigger');
      localStorage.removeItem('aura_premium_diet_pending');
      return;
    }

    // 6. DIET READY (Handled globally by DietGlobalWorker now)
    // Legacy block removed to prevent conflicts.


    // 6. WAKE UP LOGIC REMOVED (Server handles async replies naturally)
    if (localStorage.getItem('aura_marc_pending_response') === 'true') {
      // Just consume the flag so we don't spam
      localStorage.removeItem('aura_marc_pending_response');
    }
  };
  checkPremiumTriggersRef.current = checkPremiumTriggers;

  const now = new Date();
  const currentHour = now.getHours();
  // Offline between 23:00 (11 PM) and 06:00 (6 AM)
  const isOfflineTime = currentHour >= 23 || currentHour < 6;

  // Master Initialization Effect
  useEffect(() => {
    if (!user) return;

    let isMounted = true;
    const init = async () => {
      setStatus('ausente');

      const history = await loadHistory();
      if (!isMounted) return;

      await checkPremiumTriggers(history);
    };
    init();

    // Background Sync Recovery (V1000)
    // Re-fetch chat history when the app wakes up to catch messages sent while offline/backgrounded
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        console.log("[AIAssistant] App visible again. Synchronizing chat history...");
        loadHistory();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // V7002: SYNC OFFLINE QUEUE
    const syncOfflineQueue = async () => {
      if (!navigator.onLine) return;
      const queueStr = localStorage.getItem('aura_offline_chat_queue');
      if (!queueStr) return;
      try {
        const queue: Message[] = JSON.parse(queueStr);
        if (queue.length === 0) return;
        
        console.log(`[AIAssistant] Syncing ${queue.length} offline messages...`);
        for (const msg of queue) {
          // Si mock user
          if (user?.id === 'mock-user-id-123') {
            const history = JSON.parse(localStorage.getItem('aura_mock_chat_history') || '[]');
            history.push({ ...msg, created_at: new Date().toISOString() });
            localStorage.setItem('aura_mock_chat_history', JSON.stringify(history));
          } else {
             await supabase.from('chat_history').insert({
              id: msg.id,
              user_id: user?.id,
              role: msg.role,
              content: msg.content,
              seen: msg.seen
            });
          }
        }
        localStorage.removeItem('aura_offline_chat_queue');
        loadHistory();
      } catch (e) {
        console.error("[AIAssistant] Error syncing offline queue:", e);
      }
    };

    window.addEventListener('online', syncOfflineQueue);
    syncOfflineQueue();

    // Trigger on specific events, NOT a blind 3s interval (Root cause of spam)
    const handleWelcomeEvent = () => checkPremiumTriggersRef.current();

    window.addEventListener('aura_premium_welcome_trigger', handleWelcomeEvent);
    window.addEventListener('aura_premium_diet_pending', handleWelcomeEvent);
    window.addEventListener('aura_premium_diet_ready', handleWelcomeEvent);

    // V334: Force instant tick evaluation when Diet Reveal jumps
    const forceInstaReveal = () => setTick(t => t + 1);
    window.addEventListener('aura_diet_reveal_show', forceInstaReveal);

    // V333 UPDATE: Diet Completion Logic moved to DietGlobalWorker for centralized handling.
    // Time-based reveal animations are now handled by the precision Tick effect.

    // Realtime channel handled by useChatSync hook
    return () => {
      isMounted = false;
      if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (typingTimeoutResetRef.current) clearTimeout(typingTimeoutResetRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', syncOfflineQueue);
      window.removeEventListener('aura_premium_welcome_trigger', handleWelcomeEvent);
      window.removeEventListener('aura_premium_diet_pending', handleWelcomeEvent);
      window.removeEventListener('aura_premium_diet_ready', handleWelcomeEvent);
    };
  }, [user?.id, location.pathname]);

  // V97: BIDIRECTIONAL STATUS CONTROLLER (Frontend-Driven Presence Engine)
  useEffect(() => {
    const now = Date.now();
    const isOffline = new Date().getHours() >= 23 || new Date().getHours() < 7;
    const defaultStatus = isOffline ? 'offline' : 'ausente';

    // 1. Localizar el próximo mensaje que Marc va a enviar (en el futuro)
    const pendingMsgs = messages.filter(m => {
      if (m.role !== 'model' || !m.isFuture || !m.revealTime || m.revealTime <= now || m.seen) return false;
      // V120: GHOST TIMER FIX. Exclude diet messages that were already forced to be visible.
      if (m.isDietReadyMsg && isDietForceRevealed) return false;
      // Filter out typing indicator sentinels so they do not trigger presence states
      if (m.content === '[TYPING_INDICATOR]' || m.content.includes('[TYPING_INDICATOR]')) return false;
      return true;
    });
    pendingMsgs.sort((a, b) => (a.revealTime || 0) - (b.revealTime || 0));

    const nextMsg = pendingMsgs[0];

    // Helper: Mark user offline messages as seen visually and in DB
    const markUserMessagesAsSeen = () => {
      const hasUnreadUserMsg = messages.some(m => m.role === 'user' && !m.seen);
      if (hasUnreadUserMsg) {
        const unreadIds = messages.filter(m => m.role === 'user' && !m.seen).map(m => m.id).filter(Boolean) as string[];
        
        setMessages(prev => prev.map(m => m.role === 'user' ? { ...m, seen: true } : m));
        
        // 1. Attempt DB Update (May fail under RLS, but we try)
        supabase.from('chat_history').update({ seen: true }).eq('user_id', user?.id).eq('role', 'user').eq('seen', false).then();
        
        // 2. Persistent Local Cache (Guarantees seen state persists across app restarts even if DB fails)
        if (unreadIds.length > 0) {
          addLocalSeenIds(unreadIds);
        }
      }
    };

    // 1.5. Determinar si estamos en la ventana de espera del Backend (Edge Function procesando)
    // Removed legacy `isWaitingForBackend` logic since it's fully superseded by Time-Distance predictions.

    if (nextMsg && nextMsg.revealTime) {
      // V2030: Cancel active connection inertia timer if we have a new scheduled message
      if (onlineTimerRef.current) {
        clearTimeout(onlineTimerRef.current);
        onlineTimerRef.current = null;
      }

      // 2. Predecir los estados basándose puramente en la distancia temporal
      const rawContentStr = nextMsg.content || '';
      // Limpiar etiquetas ocultas para no inflar el tiempo de escritura
      const contentStr = rawContentStr.replace(/\[[A-Z_]+:[^\]]+\]/g, '').replace(/\[ACTION:[^\]]+\]/g, '').trim();
      
      // Asumimos velocidad de escritura realista idéntica a la fórmula del backend (min 3s, max 20s)
      // 50ms por caracter para que el indicador de escritura se sincronice con el delay real del servidor
      let estimatedTypingMs = Math.max(3000, Math.min(20000, contentStr.length * 50));

      let typingStart = nextMsg.revealTime - estimatedTypingMs;
      let seenStart = typingStart - 5000; // Exactamente 5 segundos antes de escribir, se conecta y los ve

      // No need for a continuous frontend override block since the edge function now 
      // natively guarantees a minimum reading buffer if it executes too fast.

      if (now >= typingStart && now < nextMsg.revealTime) {
        // STATE: ESCRIBIENDO
        if (status !== 'typing') {
          console.log(`[Presence Engine] 💬 Marc status -> TYPING (Escribiendo). Message ID: ${nextMsg.id}, Content: "${(nextMsg.content || '').slice(0, 30)}...", RevealTime: ${nextMsg.revealTime}, Now: ${now}, Diff: ${nextMsg.revealTime - now}ms`);
          setStatus('typing');
        }
        markUserMessagesAsSeen();
      } else if (now >= seenStart && now < nextMsg.revealTime) {
        // STATE: EN LÍNEA (leyendo los mensajes antes de responder)
        if (status !== 'online') {
          console.log(`[Presence Engine] 🟢 Marc status -> ONLINE (En línea). Message ID: ${nextMsg.id}, Content: "${(nextMsg.content || '').slice(0, 30)}...", RevealTime: ${nextMsg.revealTime}, Now: ${now}, Diff: ${nextMsg.revealTime - now}ms`);
          setStatus('online');
        }
        markUserMessagesAsSeen();
      } else {
        // STATE: ESPERANDO
        // FIX V2029: Inercia de conexión. Si acaba de enviar un mensaje (typing) o estaba leyendo (online),
        // mantenlo "En línea" mientras espera el tiempo para empezar a escribir el siguiente.
        // No forzar "Ausente" para volver a saltar a "Escribiendo", que da sensación de desconexión robótica.
        if (status === 'online' || status === 'typing') {
          if (status !== 'online') setStatus('online');
        } else if (status !== defaultStatus) {
           setStatus(defaultStatus);
        }
      }
    } else {
      // 3. No hay mensajes programados en el futuro cercano
      // Si antes estaba escribiendo o interactuando activamente, lo dejamos "En línea" 1 minuto para dar aspecto natural.
      if (status === 'typing' || status === 'online') {
        if (status === 'typing') {
          setOnlineTemporarily();
        }
      } else if (status !== defaultStatus) {
        setStatus(defaultStatus);
      }

      // Failsafe: Si apagaron la app y vuelven cuando Marc ya ha respondido,
      // la ventana de "seenStart" se omitió. Forzar 'seen' si ya hay respuesta visible.
      const lastUserMsgs = messages.filter(m => m.role === 'user' && !m.seen);
      if (lastUserMsgs.length > 0) {
        const lastVisibleModelMsg = visibleMessages.filter(m => m.role === 'model').pop();
        if (lastVisibleModelMsg) {
          const modelTime = lastVisibleModelMsg.revealTime || (lastVisibleModelMsg.createdAt ? new Date(lastVisibleModelMsg.createdAt).getTime() : 0);
          const needsUpdate = lastUserMsgs.some(m => {
            const userTime = m.createdAt ? new Date(m.createdAt).getTime() : 0;
            return modelTime > userTime;
          });
          
          if (needsUpdate) {
            markUserMessagesAsSeen();
          }
        }
      }
    }
  }, [messages, tick, status, isLoading, user?.id, visibleMessages]);


  // MARK AS SEEN (Robust)
  useEffect(() => {
    if (!isOpen || visibleMessages.length === 0) return;

    const timeoutId = setTimeout(async () => {
      const unreadMessages = visibleMessages.filter(m => m.role === 'model' && !m.seen);
      if (unreadMessages.length === 0) return;

      const unreadIds = unreadMessages.map(m => m.id).filter(Boolean);

      if (unreadIds.length > 0) {
        console.log(`[AIAssistant] Marking ${unreadIds.length} messages as seen (Server-Side)...`);

        // 1. Optimistic Update (Local)
        setMessages(prev => prev.map(m => unreadIds.includes(m.id) ? { ...m, seen: true } : m));
        addLocalSeenIds(unreadIds); // 1.5. Persistent Fallback Cache

        // 2. Server-Side Reliable Update (Bypasses RLS via Edge Function)
        // We restore the use of the Edge Function but triggered safely only for visible messages
        const { error } = await supabase.functions.invoke('mark-seen', {
          body: { message_ids: unreadIds, user_id: user?.id }
        });

        // V555: SIMULATION BYPASS (Mark Seen)
        if (user?.id === 'mock-user-id-123') {
          const history = JSON.parse(localStorage.getItem('aura_mock_chat_history') || '[]');
          const updatedHistory = history.map((m: Message) => unreadIds.includes(m.id) ? { ...m, seen: true } : m);
          localStorage.setItem('aura_mock_chat_history', JSON.stringify(updatedHistory));
        }

        if (error) {
          console.error("[AIAssistant] Error marking as seen (Server):", error);
          // Optional: Revert local state? No, arguably better to assume success for UI responsiveness
        }

        // 3. Update Profile Interaction Time (Independent)
        await supabase.from('profiles').update({ last_interaction: new Date().toISOString() }).eq('id', user?.id);
      }
    }, 1500); // V2202: 1.5s Debounce to prevent DB spam

    return () => clearTimeout(timeoutId);
  }, [isOpen, visibleMessages.length, user?.id]);

  // SCROLL SYNC
  const prevVisibleCount = useRef(visibleMessages.length);
  useEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (!el) return;

    // In flex-col-reverse, el.scrollTop = 0 is already the bottom.
    const addedNewMessage = visibleMessages.length > prevVisibleCount.current;
    prevVisibleCount.current = visibleMessages.length;

    if (status === 'typing' || addedNewMessage) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [status, visibleMessages.length, isOpen]);


  useEffect(() => {
    if ((isPremium || localStorage.getItem('aura_pending_welcome_sequence')) && isHistoryLoaded) {
      checkPremiumTriggers();
    }
  }, [isPremium, isHistoryLoaded, location.pathname]);

  // (The JS visualViewport hack was removed as it conflicts with modern iOS interactive-widget resizes-content meta tag, causing bouncy sliding).

  useEffect(() => {
    if (isOpen) {
      // Sync check-in review state
      if (localStorage.getItem('aura_checkin_review_active') === 'true') {
        setIsCheckinReviewActive(true);
      } else {
        setIsCheckinReviewActive(false);
      }
    }
  }, [isOpen]);

  // AUTO-OPEN CHAT REMOVED (Non-intrusive)

  // CLIENT-SIDE BRAIN FALLBACK (V200: Direct Gemini Access)
  // DELETED: Migrated to Supabase Edge Functions (process-chat)


  const saveMessageToDb = async (msg: Message) => {
    if (!user) return null;
    try {
      let imageUrl = msg.image;
      
      // Upload Base64 to Supabase Storage if present
      if (imageUrl && imageUrl.startsWith('data:image')) {
        try {
          console.log("[Client] Uploading chat image to Supabase Storage...");
          const res = await fetch(imageUrl);
          const blob = await res.blob();
          const filePath = `${user.id}/chat_${Date.now()}.jpg`;
          const { error: uploadError } = await supabase.storage
            .from('checkins')
            .upload(filePath, blob, { contentType: 'image/jpeg' });
          if (uploadError) throw uploadError;
          
          const { data } = supabase.storage.from('checkins').getPublicUrl(filePath);
          imageUrl = data.publicUrl;
          console.log("[Client] Chat image uploaded successfully:", imageUrl);
        } catch (uploadErr) {
          console.error("[Client] Failed to upload chat image to storage:", uploadErr);
          imageUrl = undefined; // Fallback to no image if upload fails
        }
      }

      const { data, error } = await supabase.from('chat_history').insert({
        id: msg.id, // V1208: Ensure deterministic stable ID to prevent Framer Motion ghosting
        user_id: user.id,
        role: msg.role,
        content: msg.content,
        image: imageUrl || null,
        seen: msg.seen
      }).select().single();

      // V555: SIMULATION SAVE
      if (user?.id === 'mock-user-id-123') {
        const mockMsg = {
          id: msg.id || generateUUIDv4(),
          user_id: user.id,
          role: msg.role,
          content: msg.content,
          seen: msg.seen,
          created_at: new Date().toISOString()
        };
        const history = JSON.parse(localStorage.getItem('aura_mock_chat_history') || '[]');
        history.push(mockMsg);
        localStorage.setItem('aura_mock_chat_history', JSON.stringify(history));
        return mockMsg;
      }

      if (error) {
        throw error;
      }
      return data;
    } catch (err: any) {
      console.error("Error saving message:", err);
      return null;
    }
  };




  // ==================================================================================
  // AGENTIC BACK-CHANNEL: Background Worker Refactored to global <DietGlobalWorker />
  // Logic removed from here to ensure persistence even when chat is closed.
  // ==================================================================================

  // LEGACY CLIENT AI FUNCTIONS REMOVED (SERVER-SIDE MIGRATION)
  // The brain is now in supabase/functions/process-chat


  // 5. MAIN SEND HANDLER (SERVER-SIDE DRIVEN)
  const isSendingRef = React.useRef(false);

  const clearChatDOM = () => {
    const el = document.getElementById('marc-chat-input') as HTMLTextAreaElement | HTMLElement;
    if (!el) return;
    
    // For textarea, blurring/focusing forces OS to commit sentence boundary tracking
    if ('value' in el) {
      if (document.activeElement === el) {
          el.blur(); 
          el.focus();
      }
      (el as HTMLTextAreaElement).value = '';
      el.style.height = 'auto'; // Reset height
    } else {
      // Fallback for contentEditable
      if (document.activeElement === el) {
        document.execCommand('selectAll', false, undefined);
        document.execCommand('delete', false, undefined);
      }
      if (el.textContent !== '') el.textContent = '';
    }
    setInput('');
  };

  const handleSend = async () => {
    // Force read from DOM to ensure we capture the absolute latest text before React state update
    const inputEl = document.getElementById('marc-chat-input') as HTMLTextAreaElement | HTMLElement;
    const latestDOM = ('value' in (inputEl || {})) ? (inputEl as HTMLTextAreaElement).value : inputEl?.textContent || '';
    const activeText = latestDOM.trim() || input.trim();

    // Strict Client-Side Debounce Base Check
    if (isSendingRef.current) return;
    if (!activeText && !selectedMedia) return;

    if (activeText === '/clear') {
      await supabase.from('chat_history').delete().eq('user_id', user?.id);
      setMessages([]);
      messagesRef.current = [];
      clearChatDOM();
      return;
    }

    if (activeText === '/debug') {
      alert("Modo Server-Side Activo. Cerebro en la Nube ☁️");
      clearChatDOM();
      return;
    }

    // DEBUG COMMAND: Force Diet Update (Bypasses AI)
    if (activeText === '/force_diet') {
      console.log("[Debug] Forcing Diet Update...");
      const { data: currentStats } = await supabase.from('profiles').select('stats').eq('id', user?.id).single();
      if (currentStats && user) {
        // V551: RESET TIMESTAMPS explicitly to prevent "Starts at 100%" bug
        // We set them to null so the Worker knows this is a FRESH request.
        await supabase.from('profiles').update({
          stats: {
            ...currentStats.stats,
            aura_diet_status: 'trigger_update',
            aura_diet_process_start: null,
            aura_target_completion_time: null
          }
        }).eq('id', user.id);
        localStorage.setItem('aura_diet_status', 'trigger_update');
        const isUpdateDebug = !!plan && Object.keys(plan).length > 0;
        localStorage.setItem('aura_generation_type', isUpdateDebug ? 'update' : 'new'); // V920: Explicitly mark for Success Screen
        window.dispatchEvent(new Event('aura_diet_updated'));

        const debugMsg: Message = { role: 'model', content: "🛠️ [SISTEMA] Diet Update Triggered Manually.", seen: true, createdAt: new Date().toISOString() };
        setMessages(prev => [...prev, debugMsg]);
      }
      clearChatDOM();
      return;
    }

    const userMsg = activeText;
    const mediaToUpload = selectedMedia;
    clearChatDOM();
    if (mediaToUpload) setSelectedMedia(null);

    // If there is media, send immediately, bypass debounce to avoid complexity
    if (mediaToUpload) {
      isSendingRef.current = true; // LOCK
      setIsLoading(true); // SET LOADING STATE
      try {
        const newMsg: Message = {
          id: generateUUIDv4(),
          role: 'user',
          content: userMsg || "📷 Imagen adjunta",
          image: mediaToUpload || undefined,
          seen: false,
          createdAt: new Date().toISOString()
        };
        setMessages(prev => [...prev, newMsg]);

        if (status === 'online' || status === 'typing') {
          setOnlineTemporarily();
        }

        const savedRecord = await saveMessageToDb(newMsg);
        if (savedRecord) {
          setMessages(prev => prev.map(m => (m.id === newMsg.id) ? { ...m, id: savedRecord.id, image: savedRecord.image || m.image } : m));
          setIsLoading(false);
          setIsLoading(false);
        } else {
          // Remove the ghost message visually since it failed to hit the DB
          setMessages(prev => prev.filter(m => m.id !== newMsg.id));
          console.error(" [Client] Error saving message to DB.");
          setIsLoading(false);
        }

        setTimeout(() => {
          if (isComponentMountedRef.current && isLoading) {
            setIsLoading(false);
            const h = new Date().getHours();
            if (!(h >= 23 || h < 7)) {
              setStatus('ausente');
            } else {
              setStatus('offline');
            }
          }
        }, 420000);
      } catch (e) {
        console.error("Error sending immediate:", e);
        setIsLoading(false);
        const h = new Date().getHours();
        if (!(h >= 23 || h < 7)) {
          setStatus('ausente');
        } else {
          setStatus('offline');
        }
      } finally {
        setTimeout(() => { isSendingRef.current = false; }, 500);
      }
      return;
    }

    // --- TEXT MESSAGES ---
    isSendingRef.current = true; // LOCK
    setIsLoading(true); // SET LOADING STATE

    try {
      const newMsg: Message = {
        id: generateUUIDv4(),
        role: 'user',
        content: userMsg,
        seen: false,
        createdAt: new Date().toISOString()
      };
      
      // V7002: OFFLINE QUEUE INTERCEPTOR
      if (!navigator.onLine) {
        console.log("[AIAssistant] Offline mode detected. Queuing message locally.");
        const queueStr = localStorage.getItem('aura_offline_chat_queue');
        const queue = queueStr ? JSON.parse(queueStr) : [];
        queue.push(newMsg);
        localStorage.setItem('aura_offline_chat_queue', JSON.stringify(queue));
        
        // Optimistic UI Update (looks like sending)
        setMessages(prev => [...prev, { ...newMsg, id: `offline_${newMsg.id}` }]);
        setIsLoading(false);
        setTimeout(() => { isSendingRef.current = false; }, 500);
        return;
      }

      // OPTIMISTIC UI UPDATE
      setMessages(prev => [...prev, newMsg]);

      if (status === 'online' || status === 'typing') {
        setOnlineTemporarily();
      }

      const savedRecord = await saveMessageToDb(newMsg);

      if (savedRecord) {
        setMessages(prev => prev.map(m => (m.id === newMsg.id) ? { ...m, id: savedRecord.id } : m));

        setIsLoading(false);
      } else {
        setMessages(prev => prev.filter(m => m.id !== newMsg.id));
        console.error(" [Client] Error saving message to DB.");
        setIsLoading(false);
      }

      setTimeout(() => {
        if (isComponentMountedRef.current && isLoading) {
          setIsLoading(false);
          const h = new Date().getHours();
          if (!(h >= 23 || h < 7)) {
            setStatus('ausente');
          } else {
            setStatus('offline');
          }
        }
      }, 420000); 
    } catch (e) {
      console.error("Error sending message:", e);
      setIsLoading(false);
      const h = new Date().getHours();
      if (!(h >= 23 || h < 7)) {
        setStatus('ausente');
      } else {
        setStatus('offline');
      }
    } finally {
      setTimeout(() => { isSendingRef.current = false; }, 500); 
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isLoading) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedMedia(reader.result as string);
      e.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  // Final visibility check
  const hideUI = ['/questionnaire', '/welcome', '/basic-welcome'].includes(location.pathname);
  if (!user || hideUI) {
    return null;
  }

  return (
    <>
      {location.pathname !== '/premium-welcome' && (
        <div style={{ position: 'fixed', bottom: 'calc(82px + max(0px, env(safe-area-inset-bottom) - 12px))', right: '16px', zIndex: 10010 }} className="relative">
          <button
            id="nav-item-chat"
            onClick={() => {
              if (!isOpen && unreadCount > 0) {
                const firstUnread = messages.find(m => m.role === 'model' && !m.seen);
                if (firstUnread?.id) {
                  setNewMsgMarkerId(firstUnread.id);
                } else {
                  // Fallback if no specific unread model message found but count > 0
                  const fallbackMsg = messages[messages.length - unreadCount];
                  setNewMsgMarkerId(fallbackMsg?.id || null);
                }
              } else if (isOpen) {
                setNewMsgMarkerId(null);
              }
              setIsOpen(!isOpen);
            }}
            className="size-14 rounded-full bg-gradient-to-tr from-primary to-orange-500 text-white flex items-center justify-center border border-white/20 group relative overflow-hidden transition-all hover:scale-110 active:scale-95 shadow-[0_0_20px_rgba(242,127,13,0.4)] hover:shadow-[0_0_30px_rgba(242,127,13,0.7)] transition-all duration-300"
          >
            {/* Shimmer Sweep Animation (Synergy with CTAs) */}
            <motion.div
              key="shimmer-slow-20"
              animate={{
                left: ['-100%', '200%'],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                repeatDelay: 5,
                ease: "easeInOut"
              }}
              className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[30deg] pointer-events-none z-0"
            />

            {/* Subtle Metallic Bevel - Internal Only */}
            <div className="absolute inset-0 border border-white/20 rounded-full pointer-events-none"></div>

            <span className="material-symbols-outlined text-3xl font-black relative z-10 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
              {isOpen ? 'close' : 'chat_bubble'}
            </span>
          </button>

          {/* UNREAD BADGE - V3.5 - Solid implementation */}
          {!isOpen && unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 size-5 bg-white rounded-full flex items-center justify-center border border-black/15 z-40 shadow-[0_0_12px_rgba(255,255,255,0.4),_0_2px_4px_rgba(0,0,0,0.2)] animate-pulse">
              <span className="text-black text-[10px] font-black leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </div>
          )}

          {!isPremium && (
            <div className="absolute -top-2 -left-2 premium-gold-gradient animate-premium-shine px-1.5 py-0.5 rounded-md border border-black/20 shadow-[0_2px_10px_rgba(212,175,55,0.4)] flex items-center gap-0.5 rotate-[-10deg] scale-90 z-20 pointer-events-none">
              <span className="material-symbols-outlined text-[10px] font-black text-black">workspace_premium</span>
              <span className="text-black text-[9px] font-black uppercase tracking-tighter">PRO</span>
            </div>
          )}
        </div>
      )}

      {isOpen && (
        <>
          {/* V-LOCK CURTAIN: Prevents iOS translucent keyboard from seeing the dashboard underneath. 
              We use a fixed massive rect anchored to the top of the body that cannot be pushed. */}
          <div className="fixed top-0 left-0 right-0 h-[150vh] bg-[#0a0a0c] z-[10010] md:hidden pointer-events-none animate-in fade-in duration-300"></div>

          <div 
            style={
              typeof window !== 'undefined' && 
              window.innerWidth < 768 && 
              viewport && 
              viewport.height < window.innerHeight - 100
                ? { 
                    height: `${viewport.height}px`, 
                    top: `${viewport.offsetTop}px`, 
                    bottom: 'auto' 
                  } 
                : undefined
            }
            className="fixed inset-0 md:inset-auto md:bottom-24 md:right-4 md:w-[400px] z-[10011] bg-[#0a0a0c] md:bg-surface-card/80 backdrop-blur-2xl md:rounded-[2.5rem] flex flex-col md:h-[75vh] max-h-none md:max-h-[800px] shadow-[0_40px_80px_rgba(0,0,0,0.6)] border-0 md:border border-white/10 animate-in fade-in md:slide-in-from-bottom-0 duration-300 overflow-hidden ring-0 md:ring-1 ring-white/5"
          >
            {/* GLOWING MESH GRADIENTS BACKGROUND (MOBILE ONLY) */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none md:hidden opacity-30">
              <div className="absolute -top-12 -left-12 size-[250px] rounded-full bg-primary/20 blur-[80px]"></div>
              <div className="absolute bottom-24 -right-12 size-[250px] rounded-full bg-orange-600/10 blur-[80px]"></div>
            </div>
          {/* HEADER */}
          <div ref={headerRef} className="flex-none relative px-5 pb-5 pt-[calc(var(--safe-top)+1.25rem)] border-b border-white/5 bg-[#0a0a0c]/90 backdrop-blur-xl flex items-center justify-between z-20">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="size-11 rounded-2xl border border-white/10 overflow-hidden shadow-lg">
                  <img src={marcAvatarUrl || "/assets/images/landing/marc.jpg"} alt="Marc" className="w-full h-full object-cover object-top" />
                </div>
                <div className={`absolute -top-1 -right-1 size-3.5 rounded-full border-[3px] border-surface-card ${status === 'offline' ? 'bg-slate-500' : status === 'ausente' ? 'bg-yellow-500 shadow-[0_0_10px_#eab308]' : 'bg-[#00ff9d] shadow-[0_0_10px_#00ff9d]'}`}></div>
              </div>
              <div>
                <span className="font-black text-white uppercase tracking-tight text-sm flex items-center gap-1 leading-none mb-1">
                  Marc Serrano
                  <span className="material-symbols-outlined text-[14px] text-blue-500" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                </span>
                <span className="text-[9px] font-black text-primary uppercase tracking-[0.2em] flex items-center gap-1.5 opacity-80">
                  {status === 'offline' ? (
                    t('ai_assistant.status.offline', 'Desconectado')
                  ) : status === 'typing' ? (
                    t('ai_assistant.status.typing', 'Escribiendo...')
                  ) : status === 'ausente' ? (
                    <> <span className="size-1 bg-yellow-500 rounded-full animate-pulse shadow-[0_0_8px_#eab308]"></span> {t('ai_assistant.status.away', 'Ausente')} </>
                  ) : (
                    <> <span className="size-1 bg-[#00ff9d] rounded-full animate-pulse shadow-[0_0_8px_#00ff9d]"></span> {t('ai_assistant.status.online', 'En línea')} </>
                  )}
                </span>
              </div>
            </div>

            <button onClick={() => {
              setIsOpen(false);
              setNewMsgMarkerId(null);
            }} className="size-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors">
              <span className="material-symbols-outlined text-lg">expand_more</span>
            </button>
          </div>

          {/* DIET UPDATE STATUS INDICATOR REMOVED AS PER REQUEST */}

          <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0 relative z-10 px-5 pt-6 pb-6 flex flex-col-reverse gap-6 bg-transparent scrollbar-hide overscroll-none">
            <AnimatePresence mode="popLayout" initial={false}>
              {status === 'typing' && (
                <motion.div
                  key="typing-indicator"
                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10, transition: { duration: 0.2 } }}
                  className="flex justify-start shrink-0"
                >
                  <div className="bg-black/40 border border-white/5 px-4 py-4 rounded-[1.5rem] rounded-tl-sm backdrop-blur-sm flex gap-1.5 items-center h-12">
                    <span className="size-1.5 bg-primary rounded-full animate-bounce"></span>
                    <span className="size-1.5 bg-primary rounded-full animate-bounce [animation-delay:0.2s]"></span>
                    <span className="size-1.5 bg-primary rounded-full animate-bounce [animation-delay:0.4s]"></span>
                  </div>
                </motion.div>
              )}

              {/* SKELETON LOADER FOR CHAT HISTORY */}
              {!isHistoryLoaded && (
                <div key="skeleton-loader-container" className="flex flex-col-reverse gap-6 w-full animate-pulse shrink-0">
                  <div className="flex justify-start w-full">
                    <div className="w-[60%] h-12 bg-white/5 border border-white/5 rounded-[1.5rem] rounded-tl-sm"></div>
                  </div>
                  <div className="flex justify-end w-full">
                    <div className="w-[45%] h-10 bg-white/10 rounded-[1.5rem] rounded-tr-sm"></div>
                  </div>
                  <div className="flex justify-start w-full">
                    <div className="w-[75%] h-16 bg-white/5 border border-white/5 rounded-[1.5rem] rounded-tl-sm"></div>
                  </div>
                </div>
              )}

              {isHistoryLoaded && [...visibleMessages].reverse().map((m, i_rev) => {
                const i = visibleMessages.length - 1 - i_rev;
                // V110: Absolute Safety Render Guard
                if (m.content === '[TYPING_INDICATOR]' || m.content.includes('[TYPING_INDICATOR]')) return null;

                  // V1206: Removing React.Fragment wrapper to allow Framer Motion layout to animate smoothly
                  // AnimatePresence requires direct children to be motion elements with unique keys
                  const itemKey = `${m.id || i}`;
                  
                  return (
                    <motion.div
                      layout
                      key={itemKey}
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -20, transition: { duration: 0.2 } }}
                      transition={{ type: "spring", stiffness: 450, damping: 28, mass: 0.8 }}
                      className="flex flex-col gap-2 w-full"
                    >
                      {m.id === newMsgMarkerId && (
                        <div id="unread-marker" className="flex items-center gap-4 my-2 opacity-60 animate-in fade-in slide-in-from-left-4 duration-500 w-full">
                          <div className="h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent flex-1"></div>
                          <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold whitespace-nowrap drop-shadow-[0_0_5px_rgba(242,127,13,0.5)]">
                            {t('ai_assistant.new_messages', 'Nuevos Mensajes')}
                          </span>
                          <div className="h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent flex-1"></div>
                        </div>
                      )}

                      <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} group w-full`}>
                        <div
                          className={`max-w-[85%] p-5 shadow-lg relative ${m.role === 'user'
                            ? 'bg-gradient-to-br from-primary to-orange-600 text-white rounded-[1.5rem] rounded-tr-sm'
                            : 'bg-black/40 border border-white/5 text-slate-200 rounded-[1.5rem] rounded-tl-sm backdrop-blur-sm'
                            }`}
                        >
                          {m.image && (
                            <div className="mb-3 rounded-xl overflow-hidden border border-white/10">
                              <img src={m.image} alt="Upload" className="w-full h-auto" />
                            </div>
                          )}
                          <p className={`text-[13px] whitespace-pre-wrap leading-relaxed break-words overflow-hidden ${m.role === 'user' ? 'font-bold' : 'font-medium text-white/80'}`}>
                            {m.content}
                          </p>
                          {m.role === 'user' && (
                            <div className="flex justify-end mt-1.5 -mr-1 items-center gap-1">
                              <span className="text-[9px] text-white/40 font-medium">
                                {m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                              <span className={`material-symbols-outlined text-[14px] tracking-tighter ${(m.seen || (m.id && delayedSeen[m.id])) ? 'text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.8)]' : 'text-white/40'}`}>
                                {(m.seen || (m.id && delayedSeen[m.id])) ? 'done_all' : 'check'}
                              </span>
                            </div>
                          )}
                          {m.role === 'model' && (
                            <div className="flex justify-end mt-1">
                              <span className="text-[9px] text-white/40 font-medium">
                                {m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
              })}
            </AnimatePresence>
          </div>

          {isPremium ? (
            <div className="pt-3 px-3 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] focus-within:pb-5 transition-[padding] duration-200 bg-[#0a0a0c]/95 border-t border-white/5 backdrop-blur-xl relative z-20">
              {selectedMedia && (
                <div className="mb-4 flex justify-start animate-in slide-in-from-bottom-2 duration-300">
                  <div className="relative group">
                    <div className="size-24 rounded-2xl overflow-hidden border-2 border-primary/50 shadow-[0_0_20px_rgba(242,127,13,0.3)] bg-white/5">
                      <img src={selectedMedia} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <button onClick={() => setSelectedMedia(null)} className="absolute -top-2 -right-2 size-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg border-2 border-background-dark">
                      <span className="material-symbols-outlined text-[14px]">close</span>
                    </button>
                  </div>
                </div>
              )}
              {hookDietStatus === 'pending_photos' ? (
                <div className="py-2 px-1">
                  <div className="flex flex-col gap-4 text-orange-500 bg-orange-500/5 border border-orange-500/20 rounded-2xl p-4">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined font-black">photo_camera</span>
                      <p className="text-[11px] font-bold leading-tight text-white/90 text-left">
                        {t('ai_assistant.status.pending_photos', "Marc Serrano necesita tus fotos corporales (frontal, lateral y trasera) para calcular tu plan. Súbelas para comenzar.")}
                      </p>
                    </div>
                    <button
                      onClick={() => setIsPhotosModalOpen(true)}
                      className="w-full py-3 premium-gold-gradient text-black font-black uppercase tracking-widest rounded-xl hover:scale-[1.02] active:scale-95 transition-all text-[10px] flex items-center justify-center gap-2 border border-black/10 shadow-[0_5px_15px_rgba(212,175,55,0.2)]"
                    >
                      <span className="material-symbols-outlined text-sm">photo_camera</span>
                      {t('ai_assistant.status.upload_photos_cta', "SUBIR FOTOS AHORA")}
                    </button>
                  </div>
                </div>
              ) : isCheckinReviewActive || (isEffectivelyGenerating && status !== 'typing') ? (
                <div className="py-2 px-1 animate-pulse">
                  <div className="flex flex-col gap-3 text-primary bg-primary/5 border border-primary/20 rounded-2xl p-4">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined font-black">
                        {isEffectivelyGenerating ? 'assignment_ind' : 'pending_actions'}
                      </span>
                      <p className="text-[11px] font-bold leading-tight">
                        {isCheckinReviewActive
                          ? t('ai_assistant.status.checkin_received', "Marc ha recibido el check in, te responderá en breves.")
                          : isEffectivelyGenerating
                            ? generationType === 'update' || hookDietStatus === 'trigger_update' || hookDietStatus === 'updating'
                              ? t('ai_assistant.status.updating_diet', "Marc Serrano está actualizando tu plan nutricional personalizado. Recibirás una notificación por chat cuando esté listo.")
                              : t('ai_assistant.status.creating_diet', "Marc Serrano está creando tu plan nutricional personalizado. Recibirás una notificación por chat cuando esté listo.")
                            : t('ai_assistant.status.processing', "Procesando respuesta...")}
                      </p>
                    </div>
                    {userRole === 'admin' && localStorage.getItem('aura_dev_mode') !== 'false' && (
                      <button
                        onClick={() => {
                          if (isCheckinReviewActive) {
                            localStorage.removeItem('aura_checkin_review_active');
                            localStorage.removeItem('aura_checkin_anchor_id');
                            setIsCheckinReviewActive(false);
                          }
                          // Note: isGenerating is read-only from hook, can't easily force-clear here without hack
                        }}
                        className="mt-2 text-[9px] uppercase tracking-widest font-black text-white/40 hover:text-white transition-colors border border-white/10 py-2 rounded-lg"
                      >
                        [DEV] Forzar Desbloqueo (Solo Check-in)
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 items-center px-1">
                  <div className="flex-1 bg-[#1a1a1c] border border-white/5 rounded-3xl min-h-[44px] flex items-center pl-3 pr-2 relative focus-within:bg-[#222] focus-within:border-white/20 transition-all duration-200">
                    <input 
                      type="file" 
                      id="marc-chat-file-input"
                      accept="image/*"
                      onChange={handleFileUpload} 
                      className="hidden" 
                      disabled={isEffectivelyGenerating || hookDietStatus === 'generating_initial' || hookDietStatus === 'processing' || isCheckinReviewActive || !!localStorage.getItem('aura_pending_welcome_sequence')}
                    />
                    <button 
                      type="button"
                      onClick={() => document.getElementById('marc-chat-file-input')?.click()}
                      disabled={isEffectivelyGenerating || hookDietStatus === 'generating_initial' || hookDietStatus === 'processing' || isCheckinReviewActive || !!localStorage.getItem('aura_pending_welcome_sequence')}
                      className="size-8 rounded-full flex items-center justify-center text-white/40 hover:text-white/80 active:scale-95 transition-all flex-shrink-0"
                    >
                      <span className="material-symbols-outlined text-[20px]">add_a_photo</span>
                    </button>

                    <textarea 
                      id="marc-chat-input"
                      disabled={isEffectivelyGenerating || hookDietStatus === 'generating_initial' || hookDietStatus === 'processing' || isCheckinReviewActive || !!localStorage.getItem('aura_pending_welcome_sequence')}
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          const domEl = document.getElementById('marc-chat-input') as HTMLTextAreaElement;
                          if ((domEl?.value || '').trim() || selectedMedia) {
                            handleSend();
                            clearChatDOM();
                          }
                        }
                      }}
                      placeholder={t('ai_assistant.input_placeholder', "Mensaje...")} 
                      enterKeyHint="send"
                      spellCheck={false}
                      autoCapitalize="sentences"
                      className="w-full bg-transparent text-[15px] leading-tight text-white outline-none focus:outline-none focus:ring-0 border-none py-[11px] px-2 max-h-[120px] overflow-y-auto scrollbar-hide self-center resize-none placeholder:text-white/30" 
                      rows={1}
                    />
                  </div>
                  
                  <AnimatePresence>
                    {(input.trim() || selectedMedia) && (
                        <motion.button 
                          initial={{ opacity: 0, scale: 0.8, x: -5 }}
                          animate={{ opacity: 1, scale: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.8, x: -5 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                          onMouseDown={(e) => e.preventDefault()} // Prevents keyboard blur
                          onClick={(e) => {
                            e.preventDefault();
                            handleSend();
                            clearChatDOM();
                          }}
                          disabled={(!input.trim() && !selectedMedia) || isGenerating || hookDietStatus === 'generating_initial' || hookDietStatus === 'processing' || isCheckinReviewActive || !!localStorage.getItem('aura_pending_welcome_sequence')} 
                          className="size-[44px] rounded-full bg-primary text-black flex flex-shrink-0 items-center justify-center shadow-[0_4px_15px_rgba(242,127,13,0.4)] mb-[2px]"
                        >
                          <span className="material-symbols-outlined text-[20px] font-black mr-0.5">send</span>
                        </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-none pt-5 px-5 pb-[calc(env(safe-area-inset-bottom)+3rem)] bg-black/80 border-t border-white/10 text-center backdrop-blur-xl relative z-20">
              <p className="text-[9px] text-white/50 uppercase tracking-[0.2em] mb-3 font-bold">{t('ai_assistant.paywall_subtitle', "Desbloquea tu máximo potencial")}</p>
              <button onClick={() => { 
                setIsOpen(false); 
                navigate('/subscription'); 
              }} className="w-full py-4 premium-gold-gradient text-black font-black uppercase text-[10px] rounded-xl tracking-widest shadow-[0_0_25px_rgba(212,175,55,0.4)]">
                {t('ai_assistant.paywall_button', "Desbloquear Marc (Prueba Gratis)")}
              </button>
            </div>
          )}
          
          <PendingPhotosModal isOpen={isPhotosModalOpen} onClose={() => setIsPhotosModalOpen(false)} />
        </div>
        </>
      )}
    </>
  );
};

export default AIAssistant;
